import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { Markdown } from '../../components/ui/Markdown';
import { createVendor, updateVendor, type Vendor, type VendorInput } from './api';

interface Props {
  /** Absent when adding a shop, present when correcting one. */
  vendor?: Vendor | null;
  onSaved: (vendor: Vendor) => void;
  onCancel: () => void;
}

/**
 * A shop and everything needed to deal with it.
 *
 * Only the name is required. A vendor is filled in over time — the name at the
 * first order, the tax number when the first invoice arrives — and demanding it
 * all up front would only teach people to type "—" into fields they cannot yet
 * answer.
 */
export function VendorForm({ vendor = null, onSaved, onCancel }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<VendorInput>({
    name: vendor?.name ?? '',
    website: vendor?.website ?? '',
    legal_name: vendor?.legal_name ?? '',
    tax_id: vendor?.tax_id ?? '',
    email: vendor?.email ?? '',
    phone: vendor?.phone ?? '',
    note: vendor?.note ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof VendorInput) => (value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const save = useMutation({
    mutationFn: () =>
      vendor ? updateVendor(vendor.id, form) : createVendor({ ...form, name: form.name.trim() }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['reference', 'vendors'] });
      onSaved(saved);
    },
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const field = (key: keyof VendorInput, label: string, type = 'text') => (
    <label>
      {label}
      <input
        type={type}
        value={(form[key] as string | null) ?? ''}
        onChange={(event) => set(key)(event.target.value)}
      />
    </label>
  );

  return (
    <form
      className="component-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!form.name.trim()) return;
        save.mutate();
      }}
    >
      <h3>{vendor ? t('purchases.editVendor') : t('purchases.addVendor')}</h3>

      <div className="component-form__row">
        <label>
          {t('purchases.vendorName')}
          <input
            value={form.name}
            onChange={(event) => set('name')(event.target.value)}
            required
          />
        </label>
        {field('legal_name', t('purchases.legalName'))}
        {field('tax_id', t('purchases.taxId'))}
      </div>

      <div className="component-form__row">
        {field('website', t('purchases.website'), 'url')}
        {field('email', t('purchases.email'), 'email')}
        {field('phone', t('purchases.phone'), 'tel')}
      </div>

      <label>
        {t('purchases.note')}
        <textarea
          value={form.note ?? ''}
          onChange={(event) => set('note')(event.target.value)}
          rows={4}
        />
        <span className="component-form__parsed">{t('common.markdownHint')}</span>
      </label>

      {error && (
        <p className="panel__error">
          {t(`purchases.errors.${error}`, t('purchases.errors.unknown'))}
        </p>
      )}

      <div className="component-form__actions">
        <button type="submit" disabled={save.isPending || !form.name.trim()}>
          {t('common.save')}
        </button>
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

/** The shop's details as reference, next to the order they explain: it is the
 *  invoice, the parcel chase and the phone call that need them, and all three
 *  happen with the order open. */
export function VendorCard({ vendor, onEdit }: { vendor: Vendor; onEdit?: () => void }) {
  const { t } = useTranslation();

  // The note is not one of these. The rest are single values that line up in a
  // column; a note is prose — often several lines of "ships Tuesdays, ask for
  // Marina" — and squeezing it into the value column of a table made the whole
  // table as tall as the note.
  const rows: [string, string | null][] = [
    [t('purchases.legalName'), vendor.legal_name],
    [t('purchases.taxId'), vendor.tax_id],
    [t('purchases.phone'), vendor.phone],
    [t('purchases.email'), vendor.email],
  ];
  const filled = rows.filter(([, value]) => value);

  return (
    <div className="vendor-card">
      <div className="vendor-card__head">
        <strong>{vendor.name}</strong>
        {vendor.website && (
          <a href={vendor.website} target="_blank" rel="noreferrer">
            {vendor.website.replace(/^https?:\/\//, '')}
          </a>
        )}
        {onEdit && (
          <button type="button" onClick={onEdit}>
            {t('components.edit')}
          </button>
        )}
      </div>
      {filled.length > 0 && (
        <dl className="panel__facts">
          {filled.map(([label, value]) => (
            <div key={label} className="panel__fact">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {vendor.note && (
        <div className="panel__description">
          <Markdown source={vendor.note} lines={2} />
        </div>
      )}
    </div>
  );
}
